<template>
    <section
        class="selectable-upsells"
        :id="id"
        :aria-labelledby="headlineText ? headingId : null"
    >
        <div v-if="eyebrow || headlineText || text" class="upsell-header">
            <p v-if="eyebrow" class="upsell-eyebrow">{{ eyebrow }}</p>
            <h3 v-if="headlineText" :id="headingId">{{ headlineText }}</h3>
            <p v-if="text" class="upsell-text">{{ text }}</p>
        </div>

        <div class="upsell-options" role="group">
            <div
                v-for="(product, index) in normalizedProducts"
                :key="product.key"
                :class="[
                    'upsell-option',
                        {
                            selected: isSelected(index),
                            celebrating: isCelebrating(index),
                            disabled: product.variantIds.length === 0,
                        },
                    ]"
                    role="checkbox"
                    :aria-checked="isSelected(index).toString()"
                    :aria-disabled="(product.variantIds.length === 0).toString()"
                    :tabindex="product.variantIds.length === 0 ? -1 : 0"
                    @click="toggleProduct(index, $event)"
                    @keydown.enter.prevent="toggleProduct(index, $event)"
                    @keydown.space.prevent="toggleProduct(index, $event)"
                >
                <span class="product-image-wrapper">
                    <img
                        v-if="product.productImage"
                        :src="product.productImage"
                        :alt="product.imageAlt || product.headline || t('upsellImageAlt')"
                    />
                </span>

                <span class="product-content">
                    <span class="product-title">{{ product.headline }}</span>

                    <span v-if="product.text" class="product-description">
                        {{ product.text }}
                    </span>

                    <span v-if="product.price || product.compareAtPrice" class="price-wrapper">
                        <span v-if="product.compareAtPrice" class="compare-price">
                            {{ product.compareAtPrice }}
                        </span>
                        <span v-if="product.price" class="price">
                            {{ product.price }}
                        </span>
                    </span>

                </span>

                <span
                    v-if="isSelected(index)"
                    class="quantity-selector"
                    :aria-label="t('upsellSelectQuantity')"
                    @click.stop
                    @keydown.stop
                >
                    <button
                        type="button"
                        class="quantity-button"
                        :aria-label="getDecreaseQuantityLabel(index)"
                        @click.stop="decreaseQuantity(index)"
                    >
                        -
                    </button>
                    <span class="quantity-value" aria-live="polite">
                        {{ getQuantity(index) }}
                    </span>
                    <button
                        type="button"
                        class="quantity-button"
                        :disabled="!canIncreaseQuantity(index)"
                        :aria-label="getIncreaseQuantityLabel(index)"
                        @click.stop="increaseQuantity(index)"
                    >
                        +
                    </button>
                </span>
                <button
                    v-else-if="product.variantIds.length > 0"
                    type="button"
                    class="add-button"
                    :aria-label="t('upsellAdd')"
                    @click.stop="selectProduct(index, $event)"
                    @keydown.stop
                >
                    <span class="add-icon" aria-hidden="true">+</span>
                    <span>{{ t('upsellAdd') }}</span>
                </button>
            </div>
        </div>

    </section>
</template>

<script>
import confetti from "canvas-confetti";
import EventBus from "../event-bus";
import { builderText, customizerText } from "../utils/customizerTexts";

export default {
    name: "SelectableUpsells",
    props: {
        id: {
            type: String,
            default: "",
        },
        eyebrow: {
            type: String,
            default: "",
        },
        headline: {
            type: String,
            default: () => customizerText("upsellHeadline"),
        },
        text: {
            type: String,
            default: "",
        },
        products: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            selectedIndexes: [],
            quantities: {},
            celebratingIndexes: [],
            celebrationTimeouts: {},
        };
    },
    computed: {
        headlineText() {
            return builderText(this.headline, "upsellHeadline", { keepEmpty: true });
        },
        headingId() {
            return `${this.id || "selectable-upsells"}-headline`;
        },
        normalizedProducts() {
            return this.products.map((product, index) => {
                const variantIds = this.parseVariantIds(product);
                const headline = this.stripLeadingPlus(product.headline);
                const price = this.stripLeadingPlus(product.price);
                const compareAtPrice = this.stripLeadingPlus(product.compareAtPrice);
                const maxQuantity = this.normalizeMaxQuantity(product.maxQuantity);
                const defaultQuantity = this.clampQuantityValue(
                    this.normalizeQuantity(product.defaultQuantity || product.quantity || 1),
                    maxQuantity
                );

                return {
                    ...product,
                    headline,
                    price,
                    compareAtPrice,
                    defaultQuantity,
                    maxQuantity,
                    key: `${index}-${variantIds.join("-") || headline || "upsell"}`,
                    variantIds,
                };
            });
        },
    },
    watch: {
        products: {
            handler() {
                this.initializeSelection();
            },
            deep: true,
            immediate: true,
        },
    },
    beforeUnmount() {
        Object.values(this.celebrationTimeouts).forEach((timeout) => {
            clearTimeout(timeout);
        });
    },
    methods: {
        t(key) {
            return customizerText(key);
        },
        stripLeadingPlus(value) {
            return typeof value === "string"
                ? value.replace(/^\+\s*/, "")
                : value;
        },
        parseVariantIds(product) {
            const rawIds = product.productIds || product.productId || "";

            return String(rawIds)
                .split(",")
                .map((id) => id.trim())
                .filter(Boolean);
        },
        initializeSelection() {
            this.selectedIndexes = this.normalizedProducts
                .map((product, index) => {
                    return product.isSelected && product.variantIds.length > 0
                        ? index
                        : null;
                })
                .filter((index) => index !== null);

            const quantities = { ...this.quantities };
            this.selectedIndexes.forEach((index) => {
                if (!quantities[index]) {
                    quantities[index] = this.normalizedProducts[index].defaultQuantity;
                }
            });
            this.quantities = quantities;

            this.emitSelection();
        },
        isSelected(index) {
            return this.selectedIndexes.includes(index);
        },
        isCelebrating(index) {
            return this.celebratingIndexes.includes(index);
        },
        toggleProduct(index, event) {
            const selectedIndexes = new Set(this.selectedIndexes);
            const wasSelected = selectedIndexes.has(index);

            if (wasSelected) {
                this.deselectProduct(index);
                return;
            }

            selectedIndexes.add(index);
            this.setQuantity(index, this.getQuantity(index));

            this.selectedIndexes = this.normalizedProducts
                .map((product, productIndex) => {
                    return selectedIndexes.has(productIndex) &&
                        product.variantIds.length > 0
                        ? productIndex
                        : null;
                })
                .filter((productIndex) => productIndex !== null);

            if (!wasSelected) {
                this.celebrateSelection(index, event?.currentTarget);
            }

            this.emitSelection();
        },
        deselectProduct(index) {
            const quantities = { ...this.quantities };
            delete quantities[index];

            this.selectedIndexes = this.selectedIndexes.filter(
                (selectedIndex) => selectedIndex !== index
            );
            this.quantities = quantities;
            this.emitSelection();
        },
        selectProduct(index, event) {
            if (this.isSelected(index)) {
                return;
            }

            this.toggleProduct(index, event);
        },
        normalizeQuantity(quantity) {
            const parsedQuantity = Number.parseInt(quantity, 10);

            return Number.isFinite(parsedQuantity) && parsedQuantity > 0
                ? parsedQuantity
                : 1;
        },
        normalizeMaxQuantity(quantity) {
            const parsedQuantity = Number.parseInt(quantity, 10);

            return Number.isFinite(parsedQuantity) && parsedQuantity > 0
                ? parsedQuantity
                : null;
        },
        clampQuantityValue(quantity, maxQuantity) {
            const normalizedQuantity = this.normalizeQuantity(quantity);

            return maxQuantity
                ? Math.min(normalizedQuantity, maxQuantity)
                : normalizedQuantity;
        },
        getQuantity(index) {
            const product = this.normalizedProducts[index];

            return this.normalizeQuantity(
                this.clampQuantityValue(
                    this.quantities[index] || product?.defaultQuantity || 1,
                    product?.maxQuantity
                )
            );
        },
        setQuantity(index, quantity) {
            const product = this.normalizedProducts[index];

            this.quantities = {
                ...this.quantities,
                [index]: this.clampQuantityValue(quantity, product?.maxQuantity),
            };
        },
        canIncreaseQuantity(index) {
            const maxQuantity = this.normalizedProducts[index]?.maxQuantity;

            return !maxQuantity || this.getQuantity(index) < maxQuantity;
        },
        getIncreaseQuantityLabel(index) {
            const maxQuantity = this.normalizedProducts[index]?.maxQuantity;

            return maxQuantity && this.getQuantity(index) >= maxQuantity
                ? customizerText("upsellMaxReached", { max: maxQuantity })
                : customizerText("upsellIncrease");
        },
        getDecreaseQuantityLabel(index) {
            return this.getQuantity(index) <= 1
                ? customizerText("upsellRemove")
                : customizerText("upsellDecrease");
        },
        increaseQuantity(index) {
            if (!this.canIncreaseQuantity(index)) {
                return;
            }

            this.setQuantity(index, this.getQuantity(index) + 1);
            this.emitSelection();
        },
        decreaseQuantity(index) {
            if (this.getQuantity(index) <= 1) {
                this.deselectProduct(index);
                return;
            }

            this.setQuantity(index, this.getQuantity(index) - 1);
            this.emitSelection();
        },
        celebrateSelection(index, target) {
            if (this.prefersReducedMotion()) {
                return;
            }

            clearTimeout(this.celebrationTimeouts[index]);

            this.celebratingIndexes = [
                ...new Set([...this.celebratingIndexes, index]),
            ];
            this.showConfetti(target);

            this.celebrationTimeouts[index] = setTimeout(() => {
                this.celebratingIndexes = this.celebratingIndexes.filter(
                    (celebratingIndex) => celebratingIndex !== index
                );
                delete this.celebrationTimeouts[index];
            }, 700);
        },
        prefersReducedMotion() {
            return window.matchMedia &&
                window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        },
        getConfettiOrigin(target) {
            if (!target) {
                return { y: 0.6 };
            }

            const rect = target.getBoundingClientRect();

            return {
                x: (rect.left + rect.width / 2) / window.innerWidth,
                y: (rect.top + rect.height / 2) / window.innerHeight,
            };
        },
        showConfetti(target) {
            const origin = this.getConfettiOrigin(target);
            const colors = [
                "#7EBBCC",
                "#F99B70",
                "#D8ECF2",
                "#57a5bb",
                "#4D92A5",
                "#FCD5C3",
                "#D99272",
                "#B37457",
                "#2B2B2B",
            ];

            confetti({
                particleCount: 75,
                spread: 64,
                startVelocity: 30,
                scalar: 0.85,
                origin,
                colors,
            });

            confetti({
                particleCount: 35,
                spread: 42,
                startVelocity: 22,
                scalar: 0.7,
                origin,
                colors,
            });
        },
        emitSelection() {
            const selectedVariantItems = this.selectedIndexes.flatMap((index) => {
                const product = this.normalizedProducts[index];
                const quantity = this.getQuantity(index);

                return product
                    ? product.variantIds.map((id) => ({ id, quantity }))
                    : [];
            });
            const uniqueVariantItems = Object.values(
                selectedVariantItems.reduce((itemsById, item) => {
                    const currentQuantity = itemsById[item.id]?.quantity || 0;

                    return {
                        ...itemsById,
                        [item.id]: {
                            id: item.id,
                            quantity: Math.max(currentQuantity, item.quantity),
                        },
                    };
                }, {})
            );

            EventBus.emit("additionalUpsellProductsSelected", uniqueVariantItems);
        },
    },
};
</script>

<style lang="scss" scoped>
@use "@/assets/customizer.vars.scss" as *;

.selectable-upsells {
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    row-gap: 24px;
    width: 100%;
    max-width: calc(100vw - 32px);
    min-width: 0;
    color: $color-new-black;

    *,
    *::before,
    *::after {
        box-sizing: border-box;
    }

    .upsell-header {
        display: flex;
        flex-direction: column;
        row-gap: 6px;
        margin-bottom: -2px;
        max-width: 100%;
        min-width: 0;
    }

    .upsell-eyebrow,
    .upsell-text {
        margin: 0;
        font-size: 15px;
        line-height: 136%;
    }

    .upsell-eyebrow {
        font-weight: 700;
        color: $color-new-black;
    }

    h3 {
        margin: 0;
        font-size: 24px;
        font-weight: 700;
        line-height: 116%;
        text-align: center;
        overflow-wrap: anywhere;
    }

    .upsell-text {
        color: $color-placeholder-text;
        overflow-wrap: anywhere;
    }

    .upsell-options {
        display: flex;
        flex-direction: column;
        row-gap: 24px;
        max-width: 100%;
        min-width: 0;
    }

    .upsell-option {
        position: relative;
        display: grid;
        grid-template-columns: 120px minmax(0, 1fr);
        grid-template-rows: auto;
        align-items: center;
        column-gap: 16px;
        row-gap: 12px;
        width: 100%;
        max-width: 100%;
        min-width: 0;
        padding: 10px;
        border: 2px solid $color-light-gray;
        border-radius: 32px;
        background: $color-white;
        color: $color-new-black;
        font: inherit;
        text-align: left;
        cursor: pointer;
        transition:
            border-color 0.3s ease,
            box-shadow 0.3s ease,
            transform 0.3s ease;

        &:hover:not(.disabled) {
            border-color: $color-new-black;
        }

        &:focus-visible {
            outline: 3px solid $color-dt-light-blue-1;
            outline-offset: 3px;
        }

        &.selected {
            border-color: $color-new-black;
            box-shadow: 0 0 0 4px rgba(43, 43, 43, 0.06);
        }

        &.celebrating {
            animation: upsell-card-celebrate 0.7s cubic-bezier(0.22, 1, 0.36, 1);
        }

        &.disabled {
            opacity: 0.55;
            cursor: not-allowed;
        }
    }

    .product-image-wrapper {
        grid-row: 1 / 4;
        position: relative;
        display: block;
        min-width: 0;

        img {
            display: block;
            width: 100%;
            border-radius: 22px;
            transition: transform 0.3s ease;
        }
    }

    .upsell-option.selected .product-image-wrapper img {
        transform: translateY(-2px) scale(1.02);
    }

    .product-content {
        display: flex;
        flex-direction: column;
        row-gap: 6px;
        grid-column: 2;
        grid-row: 1 / 4;
        max-width: 100%;
        min-width: 0;
        padding-right: 100px;
    }

    .product-title {
        display: block;
        font-size: 20px;
        font-weight: 700;
        line-height: 120%;
        overflow-wrap: anywhere;
    }

    .price-wrapper {
        position: absolute;
        top: 30px;
        right: 24px;
        display: block;
        text-align: right;
        font-size: 20px;
        font-weight: 700;
    }

    .compare-price {
        display: block;
        font-size: 16px;
        font-weight: 400;
        color: $color-placeholder-text;
        text-decoration: line-through;
    }

    .price {
        font-size: 20px;
        font-weight: 700;
    }

    .compare-price + .price {
        color: #11bd90;
    }

    .product-description {
        display: block;
        font-size: 16px;
        font-weight: 700;
        line-height: 136%;
        overflow-wrap: anywhere;
    }

    .product-description {
        color: $color-new-black;
    }

    .quantity-selector,
    .add-button {
        grid-column: 2;
        grid-row: 4;
        justify-self: end;
        display: inline-flex;
        align-items: center;
        min-height: 44px;
        border: 2px solid $color-new-black;
        border-radius: 999px;
        background: $color-white;
        overflow: hidden;
    }

    .add-button {
        column-gap: 8px;
        padding: 0 16px 0 8px;
        color: $color-new-black;
        font: inherit;
        font-size: 18px;
        font-weight: 700;
        line-height: 1;
        cursor: pointer;

        &:focus-visible {
            outline: 2px solid $color-dt-light-blue-1;
            outline-offset: 3px;
        }
    }

    .add-icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 28px;
        height: 28px;
        border: 2px solid $color-new-black;
        border-radius: 50%;
        font-size: 24px;
        font-weight: 700;
        line-height: 1;
    }

    .quantity-button {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 42px;
        height: 42px;
        border: 0;
        border-radius: 999px;
        background: transparent;
        color: $color-new-black;
        font: inherit;
        font-size: 24px;
        font-weight: 700;
        line-height: 1;
        cursor: pointer;

        &:disabled {
            opacity: 0.35;
            cursor: not-allowed;
        }

        &:focus-visible {
            outline: 2px solid $color-dt-light-blue-1;
            outline-offset: -4px;
        }
    }

    .quantity-value {
        display: inline-block;
        min-width: 24px;
        text-align: center;
        font-size: 18px;
        font-weight: 700;
        line-height: 1;
    }

    @media (max-width: $breakpoint-mobile) {
        row-gap: 24px;

        h3 {
            font-size: 20px;
        }

        .upsell-option {
            grid-template-columns: minmax(78px, 96px) minmax(0, 1fr);
            column-gap: 12px;
            row-gap: 8px;
            border-radius: 32px;
        }

        .product-image-wrapper {
            grid-row: 1 / 4;
        }

        .product-content {
            padding-right: 0;
        }

        .product-title {
            font-size: 16px;
        }

        .price-wrapper {
            position: unset;
            display: block;
            text-align: left;
            font-size: 16px;
        }

        .compare-price {
            display: inline-block;
            font-size: 14px;
        }

        .price {
            display: inline-block;
            font-size: 16px;
        }

        .compare-price + .price {
            margin-left: 4px;
        }

        .upsell-eyebrow,
        .upsell-text {
            font-size: 14px;
        }

        .product-description {
            font-size: 12px;
        }

        .quantity-selector,
        .add-button {
            grid-column: 2;
            grid-row: 4;
            justify-self: end;
            min-height: 44px;
        }

        .add-button {
            font-size: 16px;
        }

        .quantity-button {
            width: 40px;
            height: 40px;
        }
    }
}

@keyframes upsell-card-celebrate {
    0% {
        transform: scale(1);
    }

    35% {
        transform: scale(1.018);
    }

    70% {
        transform: scale(0.996);
    }

    100% {
        transform: scale(1);
    }
}

@media (prefers-reduced-motion: reduce) {
    .selectable-upsells {
        .upsell-option,
        .product-image-wrapper img {
            animation: none;
            transition: none;
        }

        .upsell-option.selected .product-image-wrapper img {
            transform: none;
        }
    }
}
</style>
